import { Clear, KeyboardArrowDown } from "@mui/icons-material";
import {
  type SxProps,
  type Theme,
  type ThemeOptions,
  buttonClasses,
  formHelperTextClasses,
  inputBaseClasses,
  inputLabelClasses,
  menuItemClasses,
  outlinedInputClasses,
  svgIconClasses,
  toggleButtonClasses,
} from "@mui/material";
import { Checkbox, CheckboxOutline } from "components/legacy/Icons/Checkbox";
import type React from "react";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";
import { breakpointSelector, colorSelector, spacingSelector } from "./selectors";
import { variantMapping } from "./typography";

const LinkBehavior = ({
  ref,
  ...props
}: Omit<RouterLinkProps, "to"> & { ref?: React.Ref<HTMLAnchorElement>; href: RouterLinkProps["to"] }) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
};

const sxOverride =
  (sx: SxProps<Theme> | ((theme: Theme) => SxProps<Theme>)) =>
  ({ theme }: { theme: Theme }) =>
    theme.unstable_sx(typeof sx === "function" ? sx(theme) : sx);

export default {
  MuiTypography: { defaultProps: { variantMapping, variant: "RegularNormal" } },
  MuiLink: { defaultProps: { component: LinkBehavior } },
  MuiButtonBase: {
    defaultProps: { disableRipple: true, LinkComponent: LinkBehavior },
    styleOverrides: { root: sxOverride({ [`.${svgIconClasses.root}`]: { color: "brand.purple" } }) },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: sxOverride((theme) => ({
        textTransform: "initial",
        ...theme.typography.SemiBoldNormal,
        borderRadius: 1,
        p: 2,
        px: 4,
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.45)",
        [`&.${buttonClasses.disabled}`]: {
          color: "background.white",
          backgroundColor: "text.mediumGray",
        },
      })),
      contained: sxOverride({
        color: "background.white",
        bgcolor: "brand.purple",
        ":hover": { bgcolor: "brand.purpleHover" },
      }),
      outlined: sxOverride((theme) => ({
        borderColor: "brand.purple",
        color: "brand.purple",
        bgcolor: "background.white",
        borderWidth: 2,
        p: `calc(${spacingSelector(2)(theme)} - 2px)`,
        px: `calc(${spacingSelector(4)(theme)} - 2px)`,
        ":hover": {
          borderColor: "brand.purpleHover",
          borderWidth: 2,
          color: "brand.purpleHover",
          bgcolor: "background.background",
        },
      })),
      text: sxOverride({
        color: "brand.purple",
        boxShadow: "none",
        [`&.${buttonClasses.disabled}`]: {
          color: "text.mediumGray",
          backgroundColor: "transparent",
        },
        ":hover": {
          color: "brand.purpleHover",
          bgcolor: "background.background",
        },
      }),
      sizeSmall: sxOverride({
        py: 1,
        px: 2,
        lineHeight: "1.6",
      }),
      outlinedSizeSmall: sxOverride((theme) => ({
        py: `calc(${spacingSelector(1)(theme)} - 2px)`,
        px: `calc(${spacingSelector(2)(theme)} - 2px)`,
      })),
      startIcon: {
        marginRight: 6,
        "> *:nth-of-type(1)": { fontSize: "1rem" },
      },
    },
  },
  MuiIconButton: { defaultProps: { disableRipple: true }, styleOverrides: { root: sxOverride({ p: 1 }) } },
  MuiSvgIcon: {
    styleOverrides: {
      root: ({ theme, ownerState }) =>
        sxOverride({
          fontSize: "1.25rem",
          color: ownerState.color === "inherit" ? "text.gray" : ownerState.color,
        })({ theme }),
      fontSizeSmall: { fontSize: "1rem" },
      fontSizeLarge: { fontSize: "1.5rem" },
      fontSizeInherit: { fontSize: "inherit" },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: "primary" as any,
      icon: <CheckboxOutline />,
      checkedIcon: <Checkbox />,
    },
    styleOverrides: { root: { outline: "none", svg: { fontSize: "1rem" } } },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: sxOverride((theme) => ({
        textTransform: "unset",
        padding: `${theme.spacing(1.5)} 0`,
        color: "text.gray",
        bgcolor: "background.background",
        "&:hover": { bgcolor: "text.lightGray" },
        border: "none",
        [`&.${toggleButtonClasses.selected}`]: {
          color: "background.white",
          bgcolor: "brand.purple",
          "&:hover": { bgcolor: "brand.purpleHover" },
        },
        [`.${svgIconClasses.root}`]: { color: "inherit" },
      })),
    },
  },
  MuiToggleButtonGroup: { defaultProps: { fullWidth: true } },
  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: sxOverride((theme) => ({
        position: "static",
        margin: 0,
        padding: 0,
        mb: 1,
        transformOrigin: 0,
        transform: "none",
        letterSpacing: "-0.02em",
        color: "text.black",
        ...theme.typography.SemiBoldNormal,
        "& span": {
          ...theme.typography.RegularNormal,
          color: "text.mediumGray",
        },
        [`&.${inputLabelClasses.disabled}`]: {
          color: "text.black",
        },
        [`&.${inputLabelClasses.error}`]: {
          color: "alerts.error",
        },
        // fixes issue with Mui v4 and v5 parallel usage
        [`&&.${inputLabelClasses.shrink}`]: {
          transform: "unset",
        },
      })),
      formControl: sxOverride({
        [`&.${inputLabelClasses.focused}`]: { color: "text.black" },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: sxOverride({
        [`&.${inputBaseClasses.multiline}`]: { p: 0 },
        [`.${svgIconClasses.root}`]: {
          color: "brand.purple",
          [`.${outlinedInputClasses.disabled} &`]: { color: "text.mediumGray" },
        },
      }),
      input: ({ theme }) => ({
        ...theme.typography.RegularLarge,
        height: "auto",
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        "&::placeholder": { color: theme.palette.text.mediumGray },
        [`&.${outlinedInputClasses.disabled}`]: { WebkitTextFillColor: theme.palette.text.black },
      }),
      notchedOutline: sxOverride({
        zIndex: -1,
        top: 0,
        borderColor: "text.mediumGray",
        bgcolor: "background.white",
        borderWidth: 2,
        [`.${outlinedInputClasses.focused} &&`]: {
          borderColor: "brand.purple",
        },
        [`.${outlinedInputClasses.disabled} &&`]: {
          borderColor: "text.mediumGray",
          bgcolor: "text.lightGray",
        },
        [`.${outlinedInputClasses.error} &&`]: {
          borderColor: "alerts.error",
        },
        legend: { display: "none" },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: sxOverride({
        margin: 0,
        marginTop: 2,
        color: "text.black",
        [`&.${formHelperTextClasses.disabled}`]: { color: "text.black" },
        [`&.${formHelperTextClasses.error}`]: { color: "alerts.error" },
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        zIndex: 1,
      },
    },
  },
  MuiFormControlLabel: {
    defaultProps: {
      componentsProps: { typography: { variant: "RegularNormal" } },
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
      autoFocus: false,
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: KeyboardArrowDown,
    },
    styleOverrides: {
      icon: ({ theme }) => ({
        color: theme.palette.brand.purple,
        right: theme.spacing(3),
        "&.Mui-disabled": {
          color: theme.palette.text.mediumGray,
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: { boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.4)" },
      list: sxOverride({ py: 1, px: 0 }),
    },
  },
  MuiMenuItem: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.RegularLarge,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        [`&.${menuItemClasses.selected}`]: { backgroundColor: "transparent" },
        [`&:hover, &.${menuItemClasses.selected}:hover, &.${menuItemClasses.focusVisible}, &.${menuItemClasses.selected}.${menuItemClasses.focusVisible}`]:
          {
            backgroundColor: theme.palette.text.lightGray,
            "&:hover": { backgroundColor: theme.palette.text.lightGray },
          },
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(4),
        paddingTop: 0,
        ...theme.typography.RegularNormal,
        [breakpointSelector("sm")(theme)]: { minWidth: 375 },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({ padding: theme.spacing(4) }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: sxOverride((theme) => ({
        "&.MuiTypography-root": { pt: 0.5, ...theme.typography.SemiBoldLarge },
      })),
    },
  },
  MuiPaper: {
    styleOverrides: { root: ({ theme }) => ({ boxShadow: theme.shadows[1] }) },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.background.white,
        color: theme.palette.text.black,
        boxShadow: theme.shadows[2],
        padding: spacingSelector(2)(theme),
        ...theme.typography.RegularSmall,
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.background.white,
        "::before": { boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.40)" },
      }),
    },
  },
  MuiSkeleton: {
    styleOverrides: { root: ({ theme }) => ({ backgroundColor: theme.palette.text.mediumGray }) },
  },
  MuiAlert: {
    styleOverrides: {
      root: sxOverride({
        alignItems: "center",
        boxShadow: "none",
        color: "background.white",
      }),
      icon: sxOverride({ p: 0, mr: 1, svg: { fontSize: "1rem", color: "inherit" } }),
      message: ({ theme }) => ({ padding: theme.spacing(0), ...theme.typography.SemiBoldSmall }),
      action: ({ theme }) => ({
        padding: 0,
        "& > *": { color: theme.palette.background.white },
        "& > .MuiIconButton-root": { padding: 0, paddingRight: theme.spacing(1.5) },
      }),
    },
    defaultProps: { variant: "filled" },
  },
  MuiAvatar: { styleOverrides: { fallback: sxOverride({ color: "background.white" }) } },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: colorSelector("text.black")(theme),
        backgroundColor: colorSelector("text.lightGray")(theme),
        ...theme.typography.SemiBoldNormal,
      }),
      labelSmall: ({ theme }) => ({ ...theme.typography.SemiBoldSmall }),
      deleteIconSmall: { fontSize: "1rem" },
      deleteIcon: ({ theme }) => ({
        fontSize: "1.25rem",
        color: colorSelector("text.black")(theme),
        ":hover": {
          color: colorSelector("text.gray")(theme),
        },
      }),
    },
    defaultProps: { deleteIcon: <Clear /> },
  },
  MuiAccordion: {
    defaultProps: { square: true },
    styleOverrides: {
      root: ({ theme }) => theme.unstable_sx({ borderRadius: 5, "::before": { display: "none" } }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: { root: ({ theme }) => theme.unstable_sx({ bgcolor: "background.background", borderRadius: 5 }) },
  },
  MuiAccordionDetails: {
    styleOverrides: { root: ({ theme }) => theme.unstable_sx({ px: 4 }) },
  },
  MuiRating: {
    styleOverrides: {
      iconEmpty: sxOverride({ [`.${svgIconClasses.root}`]: { color: "text.lightGray" } }),
      iconFilled: sxOverride({ [`.${svgIconClasses.root}`]: { color: "alerts.warning" } }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: sxOverride({
        color: "text.gray",
        ":not(&:first-of-type)": { mt: 3 },
      }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.background.white,
      }),
    },
  },
} satisfies ThemeOptions["components"];
